/*
Terminology:
╔═ 1: PortalSize ════════════╗               ╔═ 1: PortalSize ════════════╗               ╔═ 1: PortalSize ════════════╗
║                            ║               ║                            ║               ║                            ║
║ ┌─── 3: Preview area ────┐ ║               ║ ┌───┬────────────────┬───┐ ║               ║ ┌────────────────────────┐ ║
║ ├─── 4: ViewBox ─────────┤ ║               ║ 3   │                │   │ ║               ║ 3                        │ ║
║ │                        │ ║               ║ │   4                │   │ ║               ║ │       ┌─────────┐      │ ║
║ │                        │ ║               ║ │   │                │   │ ║               ║ │       │         │      │ ║
║ │                        │ ║               ║ │   │                │   │ ║               ║ │       ├─4───────┤      │ ║
║ │         Image          │ ║               ║ │   │      Image     │   │ ║               ║ │       │  Image/ │      │ ║
║ │                        │ ║               ║ │   │                │   │ ║               ║ │       │  Region │      │ ║
║ │                        │ ║               ║ │   │                │   │ ║               ║ │       ├─────────┤      │ ║
║ │                        │ ║               ║ │   │                │   │ ║               ║ │       │         │      │ ║
║ │                        │ ║               ║ │   │                │   │ ║               ║ │       └─────────┘      │ ║
║ ├────────────────────────┤ ║               ║ │   │                │   │ ║               ║ │    5: ViewBoxMargin    │ ║
║ └────────────────────────┘ ║               ║ └───┴────────────────┴───┘ ║               ║ └────────────────────────┘ ║
║      2: PortalPadding      ║               ║      2: PortalPadding      ║               ║      2: PortalPadding      ║
╚════════════════════════════╝               ╚════════════════════════════╝               ╚════════════════════════════╝
|<------------------------------------------>|
                 itemSpacing


1: Portal Size:
  is the maximum available size of the portal (screen size)

2: PortalPadding:
  extra padding to the portal size to indicate the region where something is fully visible
  It is possible to include an extra margin to stay from the side.
  Example: When there is a Header or BottomMenu visible, we define a PortalPadding to make a safe preview area.
  When PortalPadding changes, all items must be recalculated to fit the new box.

3: Preview Area:
 A safe area where we can show the preview/image without overlayed buttons

4: ViewBox:
  The rectangle to show centered in the preview area (in coordinates of the image/preview.)
  The default is the image rect = show entire image in portal region
  We could also pass a zone to center that zone in the preview area

5: ViewBoxMargins:
  An extra margin around the viewBox in the preview area.
  This can be used to add extra spacing around a zoomed area.

---------------------------------------------------------------------------
Scene graph:
---------------------------------------------------------------------------
screen                                  : What the user sees on his screen
 └─ slideshow-view-region               : Zoom to a specifc region on a slide (a unit transform is not zoomed)
     └─ slideshow-view-index            : Bring a specific slide on screen (a unit transform is the first slide)
         └─ slideshow                   : unzoomed slideshow (always a unit transform)
             ├─ slideshow-index:0       : move to a specific slide
             │   └─ slideshow-item      : scale preview to fit the portal
             └─ slideshow-index:1
                 └─ slideshow-item
----------------------------------------------------------------------------
*/

import { SceneGraphNode } from "../../scene-graph";
import { fitInRectTransform, Transform, transformRect } from "../../transform";

type ISlideShowItemConfig = object;

/** The transforms to zoom the slideshow to a specific slide or region in the item */
interface ISlideShowSceneGraph {
	/** Unit Transform that represents the screen */
	readonly screen: SceneGraphNode;

	/** Transform that brings us to a specific slide */
	readonly slideshowViewIndex: SceneGraphNode;

	/** Transform that zooms to a specic region on the item */
	readonly slideshowViewRegion: SceneGraphNode;

	/** Slideshow without zoom */
	readonly slideshow: SceneGraphNode;
}

/**
 * The transforms to get from a slideshow to the item in a specific slide
 */
export interface ISlideShowItemSceneGraph {
	/** Slideshow without zoom */
	readonly slideshow: SceneGraphNode;

	/** Transform that places the item on the right slide */
	readonly slideshowIndex: SceneGraphNode;

	/** Transform that scales and centers the item to fit in the portalSize */
	readonly slideshowItem: SceneGraphNode;
}

interface IItemInfo {
	/** Remember the: last size set by: setItemSize */
	readonly size: eXpress.math.IVector;

	/**
	 * Remember the last transform performed on the item
	 * This can be used to get the original position when clicked
	 */
	readonly transforms: ISlideShowItemSceneGraph;
}

interface IOnTransformChange {
	/** The transforms to apply */
	readonly transforms: ISlideShowSceneGraph;
}

/** The information emitted when a SlideShow item changes */
interface IOnItemTransformChange<TItem extends ISlideShowItemConfig = ISlideShowItemConfig> {
	/** Item of which the transform changes */
	readonly item: TItem;

	/** The index of the item in the array of items */
	readonly index: number;

	/** The transforms that are changed for this item */
	readonly transforms: ISlideShowItemSceneGraph;
}

/** The information emitted when a SlideShow item size changes */
interface IOnItemSizeChange<TItem extends ISlideShowItemConfig = ISlideShowItemConfig> {
	/** Item of which the size changes */
	readonly item: TItem;

	/** The index of the item in the array of items */
	readonly index: number;

	/** The size that is changed for this item */
	readonly size: eXpress.math.IVector;
}

/** The information emitted when a SlideShow item changes */
interface IOnViewChange<TItem extends ISlideShowItemConfig = ISlideShowItemConfig> {
	/** Item that comes into view */
	readonly item: TItem;

	/** The index of the item in the array of items */
	readonly index: number;

	/** The part of the item that comes into view (relative to that item size) */
	readonly viewBox: eXpress.math.IRect | undefined;
}

// #region Logging
function logBox(title: string, bgColor: string) {
	const pair = [
		`%c${title}`,
		`padding: 2px 4px; background-color: ${bgColor}; border-radius: 4px; color: #fff; margin: 1px 3px`,
	] as const;
	(pair as any).type = "box";
	return pair;
}

function isLogBox(item: any): item is ReturnType<typeof logBox> {
	return Array.isArray(item) && item.length === 2 && (item as any).type === "box";
}

function debugLogging(...args: any[]) {
	const styledText: any[] = [];
	const styling: string[] = [];
	const data: any[] = [];

	// Prefix with SlideShowBox
	args.forEach((arg) => {
		if (isLogBox(arg)) {
			styledText.push(arg[0]);
			styling.push(arg[1]);
		} else {
			data.push(arg);
		}
	});
	// eslint-disable-next-line no-console
	console.log(styledText.join(" "), ...styling, ...data);
}
// #endregion

function createSceneGraph(): ISlideShowSceneGraph {
	const screenTransformNode = new SceneGraphNode("screen", null);
	const slideShowViewRegionTransformNode = new SceneGraphNode(
		"slideshow-view-region",
		screenTransformNode,
	);
	const slideShowViewIndexTransformNode = new SceneGraphNode(
		"slideshow-view-index",
		slideShowViewRegionTransformNode,
	);
	const slideShowTransformNode = new SceneGraphNode("slideshow", slideShowViewIndexTransformNode);

	return {
		screen: screenTransformNode,
		slideshowViewRegion: slideShowViewRegionTransformNode,
		slideshowViewIndex: slideShowViewIndexTransformNode,
		slideshow: slideShowTransformNode,
	};
}

/**
 * Logic to show items in a horizontal list
 * This only accepts a list of objects and returns transforms to position each item in a horzontal list
 */
export function createSlideShow<TItem extends ISlideShowItemConfig>(slideShowEl: HTMLDivElement) {
	let showDebugMessages = false;
	function debug(...args: any[]) {
		if (!showDebugMessages) return;
		debugLogging(logBox("Slideshow", "#20c"), ...args);
	}

	/** A list of items to show, these are objects (for WeakMap) htat can hold any information with optional callback methods */
	let items: readonly TItem[] = [];

	/**
	 * The top/left point of the SlideShow element,
	 * We currently store this once as member variable so we trigger a reflow to get the value.
	 * Currently the slideshow position does not change, if it does, we should update this value (use a ResizeObserver?)
	 */
	const slideShowPosition = eXpress.math.point(slideShowEl.getBoundingClientRect());

	/** The size of the region to show */
	let portalSize = eXpress.math.vector(300, 400);

	/** The distance to stay from the edges */
	let portalPaddings = eXpress.math.margins(0);

	/** The index in center */
	let currentIndex = 0;

	/** The part of the item to show, relative to the item size  */
	let currentViewBox: eXpress.math.Rect | undefined;

	/** The distance to stay from the edges when a viewBox is used */
	let currentViewBoxMargins = eXpress.math.Margins.zero;

	const sceneGraph = createSceneGraph();

	slideShowEl.className += " slideshow";

	/** Notify caller the item that must be positioned */
	const onItemTransformChange = eXpress.core.createEventEmitter<IOnItemTransformChange<TItem>>();
	onItemTransformChange.subscribe((data) => debug("⚡", ".onItemTransformChange", data)); // No unsubscribe captured, if instance is disposed this is also disposed

	/**
	 * Notify if the size of an item is changed.
	 * This can be used to trigger a default zoom
	 */
	const onItemSizeChange = eXpress.core.createEventEmitter<IOnItemSizeChange<TItem>>();
	onItemSizeChange.subscribe((data) => debug("⚡", ".onItemSizeChange", data)); // No unsubscribe captured, if instance is disposed this is also disposed

	/**
	 * Notify the caller that the slideshow position must be updated.
	 * This module provides a setTransforms function that can be called.
	 * Not done directly by this code, so caller call decide to override or animate the values
	 * The move to an index and zoom are separate, at this way you can choose how to animate
	 * Example: First zoom-out, then move to the other item, then zoom-in
	 */
	const onTransformChange = eXpress.core.createEventEmitter<IOnTransformChange>();
	onTransformChange.subscribe((data) => debug("⚡", ".onTransformChange", data)); // No unsubscribe captured, if instance is disposed this is also disposed

	/**
	 * The Slideshow view is changed
	 * This is different than a onTransformChanged because this returns more semantic information, like selected index, zoomBox
	 * This could be use to know if visible index is changed, so we can perform some preloading of the next item
	 */
	const onViewChange = eXpress.core.createEventEmitter<IOnViewChange<TItem>>();
	onViewChange.subscribe((data) => debug("⚡", ".onViewChange", data)); // No unsubscribe captured, if instance is disposed this is also disposed

	/**
	 * We hold some meta information about the items added
	 * We store it in a WeakMap so we don't need to mutate the object we get and have to worry about cleaning this data.
	 */
	const itemsInfo = new WeakMap<TItem, IItemInfo>();

	let portalPaddingEl: HTMLDivElement | undefined;
	let viewBoxMarginsEl: HTMLDivElement | undefined;

	/**
	 * Set the CSS pixel size of one item, we need this to be able to center it in the portalRect
	 * I did not let the slideshow measure the size, at this way it is not our responsibility and we also could get notified is it would change in size
	 */
	function setItemSize(item: TItem, size: eXpress.math.IVector): void {
		// Get item Transform
		const index = items.indexOf(item);
		debug(".setItemSize()", index, size);

		// Don't accept zero sizes, these cause all kinds of NaN, infinity, etc...
		if (eXpress.math.Vector.zero.isSimilarTo(size)) return;

		// Don't emit events if the size is not changed.
		// This can prevent infinitive loop: setItemSize can emit an event that calls setItemSize again
		const currentItemInfo = getItemInfo(item);
		if (currentItemInfo && eXpress.math.vector(size).isSimilarTo(currentItemInfo.size)) return;

		// Set this variable to recalculate viewBox
		// Remark: Is it a problem a parent 'transaction' can set a viewBox that is resized here?
		shouldResizeViewBox = true;

		let itemSceneGraph: ISlideShowItemSceneGraph;

		if (currentItemInfo) {
			itemSceneGraph = currentItemInfo.transforms;
		} else {
			// Return a 'unit graph' when item is not yet calculated, so caller doesn't need to handle undefined cases
			const slideShowIndexTransformNode = new SceneGraphNode(
				"slideshow-index",
				sceneGraph.slideshow,
			);
			const slideShowItemTransformNode = new SceneGraphNode(
				"slideshow-item",
				slideShowIndexTransformNode,
			);

			itemSceneGraph = {
				slideshow: sceneGraph.slideshow,
				slideshowIndex: slideShowIndexTransformNode,
				slideshowItem: slideShowItemTransformNode,
			};
		}

		// If item size changes, itemsSpacing for all items can change --> recalculate needed
		updateSlideShow(function updateItemSize() {
			// If item is visible, the zoom (viewbox/margin) on the current item must also be updated
			itemsInfo.set(item, {
				size: eXpress.math.vector(size), // create copy and only used width/height in case of a rect

				// Transform values will be recalculated, but we keep the previous values (if available).
				// At this way the the old values can be used to rescale the viewBox if item size was changed
				transforms: itemSceneGraph,
			});
		});

		// Fire event
		onItemSizeChange.emit({
			index,
			item,
			size,
		});
	}

	/**
	 * Calculate the transform to center a spread/zone in rect
	 * @param centerItemInPortalTransform The transform that places the item in the center of a preview area
	 * @param itemSize the size of the item to center
	 * @param fitInRect the rectangle to fit in, mostly the preview area
	 */
	function getFitViewBoxInRectTransform(
		centerItemInPortalTransform: Transform,
		itemSize: eXpress.math.IVector,
		fitInRect = eXpress.math
			.rect(portalSize)
			.shrink(portalPaddings || eXpress.math.Margins.zero)
			.shrink(currentViewBoxMargins || eXpress.math.Margins.zero),
	) {
		// Scale the item
		return Transform.mul(
			// Undo transform to fit portalPaddings
			{
				x: -centerItemInPortalTransform.x / centerItemInPortalTransform.scale,
				y: -centerItemInPortalTransform.y / centerItemInPortalTransform.scale,
				scale: 1 / centerItemInPortalTransform.scale,
			},
			// Refit ZoomBox in PortalSize
			fitInRectTransform(itemSize, fitInRect),
		);
	}

	/**
	 * Calculate the transform for placing an item in the center of the portalRect
	 * @param itemSize The size of the item to center
	 * @param fitItemInRect The rect to center in
	 */
	function getFitItemInPortalTransform(
		itemSize: eXpress.math.IVector,
		fitItemInRect: eXpress.math.IRect = eXpress.math
			.rect(portalSize)
			.shrink(portalPaddings || eXpress.math.Margins.zero),
	) {
		return new Transform(fitInRectTransform(itemSize, fitItemInRect));
	}

	/**
	 * Variable will be true during a updateSlideShow transaction
	 * Is set to false before emitting events
	 */
	let isStaleData = false;
	let shouldResizeViewBox = false;

	/** A balanced scope that updates/emits once after all scopes are closed */
	const updateSlideShowScope = new eXpress.BalancedScope(
		() => {
			// eslint-disable-next-line no-console
			if (showDebugMessages) console.group("updateSlideShow");
			isStaleData = true;
			return showDebugMessages; // pass value used at the start so start and end have always the same value
		},
		(showDebug) => {
			let areItemsChanged = false;
			let result: ReturnType<typeof calculateHorizontalLayout>;

			try {
				// eslint-disable-next-line no-console
				if (showDebug) console.groupEnd();
				// Currently we only have 1 layout: Horizontal with 1 item in view
				result = calculateHorizontalLayout(shouldResizeViewBox);

				shouldResizeViewBox = false;
				if (!result) return;

				areItemsChanged = result.slideShowItemsInfo.size > 0;

				// TODO: check if items array is changed?

				// Update items and positions
				// ---------------------------
				if (areItemsChanged) {
					result.slideShowItemsInfo.forEach((itemInfo, item) => {
						if (!itemInfo) return;

						// Update transform
						itemsInfo.set(item, itemInfo);
					});
				}

				// Set new ViewBox (can be resized when Size of slideshow item is changed)
				currentViewBox = result.viewBox;
			} finally {
				isStaleData = false;
			}

			if (!result) return;

			if (areItemsChanged) {
				result.slideShowItemsInfo.forEach((itemInfo, item) => {
					if (!itemInfo) return;

					// Emit item change
					onItemTransformChange.emit({
						item,
						index: items.indexOf(item),
						transforms: itemInfo.transforms,
					});
				});
			}

			// Update Zoom
			// ------------
			onTransformChange.emit({
				transforms: result.slideShowTransforms,
			});

			// TODO: Must this intend be emitted by layouting function?
			onViewChange.emit({
				index: currentIndex,
				item: items[currentIndex],
				viewBox: currentViewBox,
			});
		},
	);

	/**
	 * Get a transform to place a viewbox in the center of the screen
	 */
	function calcViewBoxScale(
		forItem: TItem,
		{
			newViewBox,
			newPortalPaddings = portalPaddings || eXpress.math.Margins.zero,
			newViewBoxMargins = currentViewBoxMargins || eXpress.math.Margins.zero,
		}: {
			newViewBox?: eXpress.math.IVector;
			newPortalPaddings?: eXpress.math.IMargins;
			newViewBoxMargins?: eXpress.math.IMargins;
		},
	) {
		const itemInfo = getItemInfo(forItem);
		if (!itemInfo) return undefined;

		if (!newViewBox) newViewBox = itemInfo.size;

		const fitItemInRect = eXpress.math.rect(portalSize).shrink(newPortalPaddings);

		const centerItemInPortalTransform = getFitItemInPortalTransform(
			itemInfo.size,
			fitItemInRect,
		);

		const zoomToRectTransform = getFitViewBoxInRectTransform(
			centerItemInPortalTransform,
			newViewBox,
			eXpress.math.rect(portalSize).shrink(newPortalPaddings).shrink(newViewBoxMargins),
		);

		// Only scale is returned, x/y is relative to the spread (so not correct for spreadIndex > 0)
		return zoomToRectTransform.scale;
	}

	/**
	 * Calculate position and viewBox for items in a horizontal layout
	 * with only 1 item in view at once
	 * @param resizeViewBox When itemSize is changed, we need to recalculate the viewBox
	 */
	function calculateHorizontalLayout(resizeViewBox: boolean) {
		if (items.length === 0) return undefined;

		const fitItemInRect = eXpress.math
			.rect(portalSize)
			.shrink(portalPaddings || eXpress.math.Margins.zero);

		// Get Transforms to center the items in the portal
		// ------------------------------------------------
		const getMemoizedFitItemInPortalTransform = eXpress.utils.pipe((item: TItem) => {
			const itemInfo = itemsInfo.get(item);
			if (!itemInfo) return undefined;
			return getFitItemInPortalTransform(itemInfo.size, fitItemInRect);
		}, eXpress.withWeakMemoize());

		// Get transform to zoom a region in the portalRect
		// Do upfront so we can get smallest (most horizontal space)
		// -------------------------------------------------
		let zoomToRectTransform = Transform.unit;
		const currentItem = items[currentIndex];
		if (!currentItem) return undefined;
		const centerItemInPortalTransform = getMemoizedFitItemInPortalTransform(currentItem);
		if (!centerItemInPortalTransform) return undefined;

		const currentItemInfo = itemsInfo.get(currentItem);
		if (centerItemInPortalTransform && currentItemInfo) {
			const itemSize = currentItemInfo.size;
			zoomToRectTransform = getFitViewBoxInRectTransform(
				centerItemInPortalTransform,
				itemSize,
				eXpress.math
					.rect(portalSize)
					.shrink(portalPaddings || eXpress.math.Margins.zero)
					.shrink(currentViewBoxMargins || eXpress.math.Margins.zero),
			);
		}

		// Calculate item spacing
		// -----------------------
		const itemSpacing =
			(portalSize.width - zoomToRectTransform.x + 10) / zoomToRectTransform.scale -
			(centerItemInPortalTransform?.x || 0);

		sceneGraph.slideshow.removeChildren();

		// Update items and positions
		// ---------------------------
		/** Changed items */
		const newItemInfos = new Map<TItem, IItemInfo>();
		items.forEach((item, index) => {
			// use a copy of items to make sure not changed?
			const placeInPortalTransform = getMemoizedFitItemInPortalTransform(item);
			if (!placeInPortalTransform) return;

			const itemInfo = itemsInfo.get(item);
			if (!itemInfo) return;

			const prevMoveToIndexTransform = itemInfo.transforms.slideshowIndex.transform;
			const prevPlaceInPortalTransform = itemInfo.transforms.slideshowItem.transform;

			/** The transform that moves to the slideshow index */
			const moveToIndexTransform = new Transform({
				x: (itemSpacing || 0) * index,
			});
			const moveToIndexTransformNode = itemInfo.transforms.slideshowIndex
				? itemInfo.transforms.slideshowIndex
				: new SceneGraphNode("slideshow-index", sceneGraph.slideshow);
			moveToIndexTransformNode.setParent(sceneGraph.slideshow); // removeChildren removes parent, restore
			moveToIndexTransformNode.setTransform(moveToIndexTransform);

			/** The transform that fits a region (viewBox) in the portal */
			const placeInPortalTransformNode = itemInfo.transforms.slideshowItem
				? itemInfo.transforms.slideshowItem
				: new SceneGraphNode("slideshow-item", moveToIndexTransformNode);
			placeInPortalTransformNode.setTransform(placeInPortalTransform);

			// Set item transform
			const itemSceneGraph: ISlideShowItemSceneGraph = {
				slideshow: sceneGraph.slideshow,
				slideshowIndex: moveToIndexTransformNode,
				slideshowItem: placeInPortalTransformNode,
			};

			// Skip if nothing is changed
			if (
				moveToIndexTransform.isSimilarTo(prevMoveToIndexTransform) &&
				placeInPortalTransform.isSimilarTo(prevPlaceInPortalTransform)
			)
				return;

			// Build list of changed items
			newItemInfos.set(item, {
				...itemInfo,
				transforms: itemSceneGraph,
			});
		});

		// Remark: Should we separate viewBox logic?
		// This will be probabbly be the same for different layouts (vertical, horizontal, stack layout)

		// Update ViewBox on item resize
		// ------------------------------
		// If the itemSize of the selected item is changed, we also need to correct the viewBox for the new size
		let newViewBox = currentViewBox;
		if (resizeViewBox && newViewBox && currentItemInfo) {
			const newItemInfo = newItemInfos.get(currentItem);
			if (newItemInfo) {
				const previousItemTransform =
					currentItemInfo.transforms.slideshowItem.getTransformTo(
						currentItemInfo.transforms.slideshow,
					);
				const newItemTransform = newItemInfo.transforms.slideshowItem.getTransformTo(
					newItemInfo.transforms.slideshow,
				);

				const diffTransform = {
					x: previousItemTransform.x - newItemTransform.x, // TODO: not tested, when aspect ratio changes we can have a different x
					y: previousItemTransform.y - newItemTransform.y, // TODO: not tested, when aspect ratio changes we can have a different y
					scale: previousItemTransform.scale / newItemTransform.scale,
				};

				// After resize, viewbox will not be correct
				// Can we recalculate it here using previous and new values? or let parent specify a new viewBox?
				// Trigger update (broacast message)
				newViewBox = transformRect(newViewBox, diffTransform);
			}
		}

		// Make sure the new ViewBox is not bigger than the current preview item
		// (to prevent a part of the next preview image is visible on when previewBox is resized)
		if (currentItemInfo && newViewBox) {
			newViewBox = newViewBox.intersect(eXpress.math.rect(currentItemInfo.size));
		}

		// Calculate Slideshow transforms
		// -------------------------------
		const viewBoxTransform =
			centerItemInPortalTransform && currentItemInfo
				? getFitViewBoxInRectTransform(
						centerItemInPortalTransform,
						newViewBox || currentItemInfo.size,
						eXpress.math
							.rect(portalSize)
							.shrink(portalPaddings || eXpress.math.Margins.zero)
							.shrink(currentViewBoxMargins || eXpress.math.Margins.zero),
				  )
				: Transform.unit;
		sceneGraph.slideshowViewRegion.setTransform(viewBoxTransform);

		const slideShowViewIndexTransform = new Transform({
			x: (itemSpacing || 0) * -currentIndex,
		});
		sceneGraph.slideshowViewIndex.setTransform(slideShowViewIndexTransform);

		return {
			itemSpacing,
			viewBox: newViewBox,
			slideShowItemsInfo: newItemInfos,
			slideShowTransforms: sceneGraph, // Return Immutable / Cloned?
		};
	}

	/** Update the slideshow as one transaction */
	function updateSlideShow<T>(fn: () => T): T {
		return updateSlideShowScope.doing(fn, {
			callerInfo: "updateSlideShow",
		});
	}

	/**
	 * Move/Zoom the slideshow
	 * We don't automatically apply it, so parent can use it for
	 * - panning
	 * - pinching
	 * - control min/max zooms
	 * - boundary checks
	 * - ...
	 */
	function setTransforms(transforms: {
		slideshowViewIndexTransform: Transform;
		slideshowViewRegionTransform: Transform;
	}) {
		debug(".setTransforms()", sceneGraph);

		sceneGraph.slideshowViewIndex.setTransform(transforms.slideshowViewIndexTransform);
		sceneGraph.slideshowViewRegion.setTransform(transforms.slideshowViewRegionTransform);

		// Update only the zoom on the slideshow
		sceneGraph.slideshow.getTransformTo(sceneGraph.screen).setCssTransform(slideShowEl);
	}

	function getTransforms(): ISlideShowSceneGraph {
		if (isStaleData) {
			const msg =
				"You cannot call getTransforms() inside an updateSlideShow transaction because transforms are only recalculated at the end.";
			throw new eXpress.Exception(msg);
		}

		return sceneGraph; // TODO: return Clone / immutable?
	}

	/** Convert a screen point to the selected slideshow item coordinates */
	function getSlideShowItemAtPoint(pointInScreenSpace: eXpress.math.Point):
		| {
				item: TItem;
				index: number;
				/** Point in slideshow item space, depends on size of item */
				point: eXpress.math.Point;
		  }
		| undefined {
		debug(".getSlideShowItemAtPoint()", pointInScreenSpace);

		// We use the current selected slideshow item for the transforms
		// Previously we checked the on which slideshow item was clicked,
		// but that did not work when clicked outside the rendered item
		// Now we only show 1 preview at a time, we could later try to reimplement this
		const item = items[currentIndex];
		const itemInfo = getItemInfo(items[currentIndex]);
		if (!itemInfo) return undefined;

		const { slideshowItem } = itemInfo.transforms;

		// TODO: Shouldn't this offset be part of the scenegraph?
		const offset = new Transform({
			x: slideShowPosition.left,
			y: slideShowPosition.top,
		});

		const screenToItemTransform = slideshowItem
			.getTransformTo(sceneGraph.screen)
			.mul(offset)
			.inverse();

		// Position in item space
		const itemPosition = screenToItemTransform.transformPoint(pointInScreenSpace);

		return {
			item,
			index: currentIndex,
			point: itemPosition,
		};
	}

	function getItemInfo(item: TItem) {
		debug(".getItemInfo()", item);

		if (isStaleData) {
			const msg =
				"You cannot call getItemInfo() inside an updateSlideShow transaction because itemInfo is only recalculated and updated at the end.";
			throw new eXpress.Exception(msg);
		}
		return itemsInfo.get(item);
	}

	function updateDebugBoxes() {
		const portalRect = eXpress.math.rect(portalSize).shrink(portalPaddings);
		if (portalPaddingEl) {
			portalPaddingEl.style.left = `${portalRect.left}px`;
			portalPaddingEl.style.top = `${portalRect.top}px`;
			portalPaddingEl.style.width = `${portalRect.width}px`;
			portalPaddingEl.style.height = `${portalRect.height}px`;
			// Adjust only first node (=text)
			portalPaddingEl.childNodes[0].nodeValue =
				`portalPaddings:\n{l:${Math.round(portalPaddings.left)},t:${Math.round(
					portalPaddings.top,
				)},` +
				`r:${Math.round(portalPaddings.right)},b:${Math.round(portalPaddings.bottom)}}`;
		}

		if (viewBoxMarginsEl) {
			const left = currentViewBoxMargins?.left ?? 0;
			const top = currentViewBoxMargins?.top ?? 0;
			const right = currentViewBoxMargins?.right ?? 0;
			const bottom = currentViewBoxMargins?.bottom ?? 0;
			const viewBoxRect = portalRect.shrink(currentViewBoxMargins);

			viewBoxMarginsEl.style.left = `${viewBoxRect.left}px`;
			viewBoxMarginsEl.style.top = `${viewBoxRect.top}px`;
			viewBoxMarginsEl.style.width = `${viewBoxRect.width}px`;
			viewBoxMarginsEl.style.height = `${viewBoxRect.height}px`;
			viewBoxMarginsEl.textContent = `viewBoxMargins:\n{l:${left},t:${top},r:${right},b:${bottom}}`;
		}
	}

	/**
	 * I think this function is wrong, it should be getItemToSlideshowTransform
	 * (or return the inverse of the current result?)
	 */
	function getSlideShowToItemTransform(slideShowItem: TItem) {
		debug(".getSlideShowToItemTransform()", slideShowItem);

		const itemInfo = getItemInfo(slideShowItem);
		if (!itemInfo) return undefined;

		const { slideshowItem } = itemInfo.transforms;
		return slideshowItem.getTransformTo(sceneGraph.screen);
	}

	const slideShow = {
		el: slideShowEl,

		get items(): readonly TItem[] {
			return items;
		},

		set items(newItems: readonly TItem[]) {
			debug(".items", `${newItems.length} items`);
			updateSlideShow(() => {
				items = newItems;
			});
		},

		/** The size of the region to show */
		get portalSize() {
			return portalSize;
		},
		set portalSize(newSize: eXpress.math.IVector) {
			if (portalSize.isSimilarTo(newSize)) return;
			debug(".portalSize", portalSize);
			updateSlideShow(() => {
				portalSize = eXpress.math.vector(newSize);
			});
			updateDebugBoxes();
		},

		/** get the selected item */
		get index() {
			return currentIndex;
		},

		/** set the selected item */
		set index(newIndex: number) {
			debug(".index", newIndex);
			updateSlideShow(() => {
				const correctedIndex = Math.max(Math.min(newIndex, items.length - 1), 0);
				currentIndex = correctedIndex;
			});
		},

		/** The region to center in the portal, relative to the item size */
		get viewBox() {
			return currentViewBox;
		},
		set viewBox(rect: eXpress.math.IRect | undefined) {
			if (
				currentViewBox === rect ||
				(currentViewBox !== undefined && rect && currentViewBox.isSimilarTo(rect))
			)
				return;
			debug(".viewBox", rect);

			updateSlideShow(() => {
				currentViewBox = rect ? eXpress.math.rect(rect) : undefined;
			});
		},

		/** The distance to stay from the portal edges when a viewBox is specified */
		get viewBoxMargins() {
			return currentViewBoxMargins;
		},
		set viewBoxMargins(margins: eXpress.math.IMargins) {
			if (currentViewBoxMargins.isSimilarTo(margins)) return;
			debug(".viewBoxMargins", margins);
			updateSlideShow(() => {
				currentViewBoxMargins = eXpress.math.margins(margins);
			});
			updateDebugBoxes();
		},

		/** The distance to stay from the portal edges when no viewBox is specified */
		get portalPaddings() {
			return portalPaddings;
		},
		set portalPaddings(newPaddings: eXpress.math.IMargins) {
			if (portalPaddings.isSimilarTo(newPaddings)) return;
			debug(".portalPaddings", newPaddings);

			updateSlideShow(() => {
				portalPaddings = eXpress.math.margins(newPaddings);
			});
			updateDebugBoxes();
		},

		calcViewBoxScale,

		get showDebugBoxes() {
			return !!portalPaddingEl;
		},
		set showDebugBoxes(enable: boolean) {
			debug(".showDebugBoxes", enable);
			if (enable) {
				if (!viewBoxMarginsEl) {
					viewBoxMarginsEl = document.createElement("div");
					viewBoxMarginsEl.classList.add("slideshow__view-box-margins");
					slideShowEl.insertAdjacentElement("afterend", viewBoxMarginsEl);
				}
				if (!portalPaddingEl) {
					portalPaddingEl = document.createElement("div");
					portalPaddingEl.classList.add("slideshow__portal-padding");
					portalPaddingEl.textContent = "portalPaddings:"; // add a node for the text
					slideShowEl.insertAdjacentElement("afterend", portalPaddingEl);
				}

				updateDebugBoxes();
			} else {
				portalPaddingEl?.remove();
				viewBoxMarginsEl?.remove();
				portalPaddingEl = undefined;
				viewBoxMarginsEl = undefined;
			}
		},

		get debug() {
			return showDebugMessages;
		},

		set debug(value: boolean) {
			showDebugMessages = !!value;
		},

		updateSlideShow,

		setItemSize,

		getTransforms,

		/**
		 * Update the slideShow with the specified transformations
		 * This only changed the view of the preview.
		 * Normally you should prefer using the viewBox, this will:
		 * - broadcast change events so related items can also update (selectionbox, overlay, ...)
		 * - viewBox represents the region to keep in view when preview area changes (BottomMenu changes)
		 */
		setTransforms,

		getSlideShowItemAtPoint,

		getSlideShowToItemTransform,

		getItemInfo,

		/**
		 * Notify if slideshow transform change is requested, example when
		 * - panning preview
		 * - pinching preview
		 * - interpolation can be done for animation
		 * You can call slideShow.setTransforms/animateSlideShow to update the slideshow
		 */
		onTransformChange,

		/**
		 * Notify if the transform change of a single slideshow item (example: spread) is requested
		 * This is not done by the slideshow because some UI layers need to be updated in a different way
		 * - with transform (CSS transform on an image)
		 * - with left, top, width, height (selection box)
		 * - with rerender (canvas rendering of spread)
		 * - interpolation can be done for animation
		 */
		onItemTransformChange,

		/**
		 * Notify if the size of a single item changes
		 * This can be used to:
		 * -  recalculate the region to zoom
		 */
		onItemSizeChange,

		/** Can be used for preloading, based on index */
		onViewChange,
	};
	// slideShow.debug = true;
	// slideShow.showDebugBoxes = true;
	return slideShow;
}

// Generics on typeof does not work,
// only specified members with TItem interface
export interface ISlideShow<TItem extends ISlideShowItemConfig = ISlideShowItemConfig>
	extends Omit<
		ReturnType<typeof createSlideShow>,
		| "items"
		| "setItemSize"
		| "getSlideShowItemAtPoint"
		| "getItemInfo"
		| "getSlideShowToItemTransform"
		| "on"
		| "calcViewBoxScale"
	> {
	items: readonly TItem[];

	setItemSize(item: TItem, size: eXpress.math.IVector): void;

	getSlideShowItemAtPoint(pointInScreenSpace: eXpress.math.Point):
		| {
				item: TItem;
				index: number;
				/** Point in slideshow item space, depends on size of item */
				point: eXpress.math.Point;
		  }
		| undefined;

	getItemInfo(item: TItem): IItemInfo | undefined;

	getSlideShowToItemTransform(slideShowItem: TItem): Transform | undefined;

	calcViewBoxScale(
		forItem: TItem,
		options: {
			newViewBox?: eXpress.math.IVector;
			newPortalPaddings?: eXpress.math.IMargins;
			newViewBoxMargins?: eXpress.math.IMargins;
		},
	): number | undefined;

	onItemTransformChange: eXpress.core.IEventEmitter<IOnItemTransformChange<TItem>>;
	onItemSizeChange: eXpress.core.IEventEmitter<IOnItemSizeChange<TItem>>;
	onTransformChange: eXpress.core.IEventEmitter<IOnTransformChange>;
	onViewChange: eXpress.core.IEventEmitter<IOnViewChange<TItem>>;
}
